import { t, Trans } from '@lingui/macro';
import { formatMonetaryValue, transEnum } from '@luminovo/commons';
import { colorSystem, MultiProgressBar, Text } from '@luminovo/design-system';
import { NegotiationLineItemStatus } from '@luminovo/http-client';
import { Box, Skeleton } from '@mui/material';
import * as React from 'react';
import { LayoutCard } from '../../../components/LayoutCard';
import { useNegotiationsContext } from '../context/NegotiationsContext';
import { useNegotiationAggregatedStatuses, useNegotiationLineItems } from '../hooks/negotiationHandlers';
import { negotiationLineItemStatusTranslations } from '../i18n';
import {
    calculateNegotiationLineItemQuoteRequestSummary,
    getNegotiationLineItemStatusRawColor,
} from '../model/getNegotiationLineItemStatus';
import { getScenarioPricingDiff } from '../model/getScenarioPricingDiff';
import { getScenarioTotalVolume } from '../model/getScenarioTotalVolume';
import { LabelPercentageChange } from './LabelPercentageChange';

export function CardSummary({ negotiationId }: { negotiationId: number }) {
    const { referenceScenario, selectedScenario } = useNegotiationsContext();
    const referenceScenarioId = referenceScenario?.id;
    const selectedScenarioId = selectedScenario?.id;
    const { data: lineItems = [], isLoading } = useNegotiationLineItems(negotiationId);
    const referenceVolume = getScenarioTotalVolume(lineItems, referenceScenarioId);
    const selectedVolume = getScenarioTotalVolume(lineItems, selectedScenarioId);

    const {
        savings,
        increase,
        result: net,
        resultRatio,
    } = getScenarioPricingDiff(lineItems, referenceScenarioId, selectedScenarioId);

    return (
        <LayoutCard title={undefined}>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'auto auto auto auto auto', rowGap: 1, columnGap: 2 }}>
                <Text variant="h5" color={colorSystem.neutral[7]}>
                    <Trans>Reference volume</Trans>
                </Text>
                <Text variant="h5" color={colorSystem.neutral[7]}>
                    <Trans>Selected volume</Trans>
                </Text>
                <Text variant="h5" color={colorSystem.neutral[7]}>
                    <Trans>Total savings</Trans>
                </Text>
                <Text variant="h5" color={colorSystem.neutral[7]}>
                    <Trans>Total increase</Trans>
                </Text>
                <Text variant="h5" color={colorSystem.neutral[7]}>
                    <Trans>Result</Trans>
                </Text>

                <Text
                    variant="h1"
                    style={{
                        whiteSpace: 'nowrap',
                    }}
                    color={colorSystem.neutral[9]}
                >
                    {isLoading ? <Skeleton width={80} /> : formatMonetaryValue(referenceVolume, 'estimate')}
                </Text>
                <Text
                    variant="h1"
                    style={{
                        whiteSpace: 'nowrap',
                    }}
                    color={colorSystem.neutral[9]}
                >
                    {isLoading ? <Skeleton width={80} /> : formatMonetaryValue(selectedVolume, 'estimate')}
                </Text>
                <Text
                    variant="h1"
                    style={{
                        whiteSpace: 'nowrap',
                    }}
                    color={colorSystem.neutral[9]}
                >
                    {isLoading ? (
                        <Skeleton width={80} />
                    ) : (
                        formatMonetaryValue(savings, 'estimate', { alwaysShowSign: true })
                    )}
                </Text>
                <Text
                    variant="h1"
                    style={{
                        whiteSpace: 'nowrap',
                    }}
                    color={colorSystem.neutral[9]}
                >
                    {isLoading ? (
                        <Skeleton width={80} />
                    ) : (
                        formatMonetaryValue(increase, 'estimate', { alwaysShowSign: true })
                    )}
                </Text>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Text
                        variant="h1"
                        style={{
                            whiteSpace: 'nowrap',
                        }}
                        color={colorSystem.neutral[9]}
                    >
                        {isLoading ? (
                            <Skeleton width={80} />
                        ) : (
                            formatMonetaryValue(net, 'estimate', { alwaysShowSign: true })
                        )}
                    </Text>
                    <LabelPercentageChange percent={resultRatio} />
                </Box>
            </Box>

            <ChartNegotiationLineItemStatus negotiationId={negotiationId} />
        </LayoutCard>
    );
}

export function ChartNegotiationLineItemStatus({ negotiationId }: { negotiationId: number }) {
    const { data: aggregatedStatuses, isLoading: isLoadingAggregatedStatuses } =
        useNegotiationAggregatedStatuses(negotiationId);
    const { data: lineItems = [], isLoading: isLoadingLineItems } = useNegotiationLineItems(negotiationId);

    const data = React.useMemo(() => {
        const statusData = lineItems.map(
            (li) =>
                calculateNegotiationLineItemQuoteRequestSummary(
                    aggregatedStatuses?.statuses_by_negotiation_line_item.find(
                        (statuses) => statuses.negotiation_line_item_id === li.id,
                    ),
                ).status,
        );

        return Object.values(NegotiationLineItemStatus).map((status) => {
            return {
                id: status,
                label: transEnum(status, negotiationLineItemStatusTranslations),
                value: statusData?.filter((d) => d === status).length ?? 0,
                color: getNegotiationLineItemStatusRawColor(status),
            };
        });
    }, [aggregatedStatuses, lineItems]);

    return (
        <MultiProgressBar
            title={t`Progress`}
            data={data}
            isLoading={isLoadingAggregatedStatuses || isLoadingLineItems}
            showTotalCount={false}
            getColor={(data) => data.color}
            getLabel={(data) => data.label}
            getValue={(data) => data.value}
        />
    );
}
